import classNames from 'classnames';
import * as React from 'react';
import { Key } from 'ts-keycode-enum';
import navButtonStyles from '../Shared/Styles/NavButton.module.css';
import ExecutionContext from '~/Features/ExecutionContext';
import { Button } from '~/Partials/Button/Button';
import type { NavButtonProps } from '~/Partials/Nav/Components/NavButtons/NavButtons';
import { NavItem } from '~/Partials/Nav/NavItem';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { redirect } from '~/Partials/Utils/utils';
import styles from './SearchButton.module.css';

interface SearchButtonProps extends NavButtonProps {
    isFirstPaint: boolean;
    isOpened: (navItem: NavItem) => boolean;
    toggleItem: (navItem: NavItem) => void;
    searchPageUrl: string;
    translations: {
        search: string;
        openSearch: string;
        closeSearch: string;
        inputSearchPhrase: string;
    };
}

export const SearchButton: React.FC<React.PropsWithChildren<SearchButtonProps>> = props => {
    const { isFirstPaint, isOpened, toggleItem, translations, searchPageUrl, variant } = props;

    const searchInputRef = React.createRef<HTMLInputElement>();

    const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === Key.Enter) {
            redirectToSearch(e);
        }
    };

    const redirectToSearch = (e: any) => {
        e.preventDefault();
        const searchPhrase: HTMLInputElement | null = searchInputRef.current;

        if (!searchPhrase || !searchPhrase.value || ExecutionContext.IsServerSide()) {
            return;
        }

        const studySearchUrlWithSearchPhrase = `${searchPageUrl}?phrase=${encodeURIComponent(searchPhrase.value)}`;
        redirect(studySearchUrlWithSearchPhrase);
    };

    const onSearchElementFocus = () => {
        if (!isOpened(NavItem.Search)) {
            toggleItem(NavItem.Search);
        }
    };

    const handleTransitionEnd = () => {
        if (isOpened(NavItem.Search)) {
            searchInputRef.current?.focus();
        }
    };

    return (
        <div
            className={classNames(
                styles.SearchButton,
                !isFirstPaint &&
                    (isOpened(NavItem.Search)
                        ? [styles['Variant--opened'], styles['Variant--light']]
                        : styles['Variant--closed'])
            )}
            role="search"
        >
            <div className={styles.Container}>
                <Button
                    onClick={() => toggleItem(NavItem.Search)}
                    aria-haspopup="true"
                    aria-expanded={isOpened(NavItem.Search)}
                    className={classNames(
                        navButtonStyles.ButtonIcon,
                        styles.NavigationButton,
                        variant && navButtonStyles[`Variant--${variant}`]
                    )}
                    tabIndex={-1}
                >
                    <span className="visuallyHidden">
                        {isOpened(NavItem.Search) ? translations.closeSearch : translations.openSearch}
                    </span>
                    <SvgIcon iconName={isOpened(NavItem.Search) ? 'close' : 'search'} />
                </Button>
                <form className={styles.Dropdown}>
                    <div className={styles.Inner}>
                        <label htmlFor="SearchButtonInput" className="visuallyHidden">
                            {translations.inputSearchPhrase}
                        </label>
                        <input
                            className={styles.Input}
                            id="SearchButtonInput"
                            type="text"
                            ref={searchInputRef}
                            onKeyPress={e => handleSearchInputKeyDown(e)}
                            placeholder={isOpened(NavItem.Search) ? translations.search : ''}
                            onFocus={onSearchElementFocus}
                            onTransitionEnd={handleTransitionEnd}
                        />
                        <Button
                            htmltype="submit"
                            onClick={e => redirectToSearch(e)}
                            onFocus={onSearchElementFocus}
                            className={styles.SearchButtonAction}
                        >
                            <span className="visuallyHidden">{translations.search}</span>
                            <SvgIcon iconName="search" />
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};
